import React, { useState, useEffect } from "react";
import "../../App.css";
import { Box } from "@chakra-ui/react";
import { createModel, updateModel, getMetadataDependency, updateKGColumn } from "../../services/DataModelService";
import OntologyMapping from "../../components/configuration/OntologyMapping";
import { ColumnInfo, Header, ModelInfo } from "build-ts/metadata";
import { DataType, ColumnType, AggregationType, dataTypeFromJSON, columnTypeFromJSON, aggregationTypeFromJSON, MetadataTypeEnum } from "build-ts/enums";
import showToast from "hooks/useCustomToast";
import { KGColumnProto } from "build-ts/knowledege_graph";
import { CreateOrUpdateMetrics_Request, DependencyProto, DependencyRequestProto } from "build-ts/interface";
import { createMetric } from "services/MetricService";
import { useNavigate, useMatch } from "react-router-dom";
import { getModelById } from "services/DataModelService";
import { useConfigurationContext } from "context/ConfigurationContext";

/**
 *
 * Input: modelInfo object containing information about the data model.
 *        navigateTo(set to true) callback to navigate to another component from parent component.
 * Return: Renders the AddDescription component with relevant props.
 *
 * Handles user input for updating model information, including header, columns, and descriptions.
 * Submits the updated model information to the API. 
 *
 * @component
 */

const OntologyMappingContainer: React.FC<{
  modelInfo?: ModelInfo | undefined;
  updateFlag: boolean;
  onBack: () => void;
  onReloadModelList?: () => void;
  addedColumns?: ColumnInfo[] | undefined;
  removedColumns?: ColumnInfo[] | undefined;
}> = ({ modelInfo: propModelInfo, updateFlag, onBack, onReloadModelList, addedColumns, removedColumns }) => {
  const navigate = useNavigate();
  const match = useMatch("/mkg/models/:id");

  const id = match ? match.params.id : "";

  const { kgColumns, fetchKgColumns, modelInfoMap, setModelInfoMap, dependencyMap, setDependencyMap } = useConfigurationContext();

  // keep a local modelInfo, seeded from prop if provided
  const [modelInfo, setModelInfo] = useState<ModelInfo | undefined>(propModelInfo ?? undefined);
  const [loading, setLoading] = useState<boolean>(false);

  // State to store the header of Data Model
  const [header, setHeader] = useState<Header>(modelInfo?.header! || {});
  const [buttonLoader, setButtonLoader] = useState<boolean>(false);

  // State to store the columns of Data Model
  const [columns, setColumns] = useState<ColumnInfo[]>(modelInfo?.columns || []);
  // Request list for Creating a new Metrics
  const [metricRequestList, setMetricRequestList] = useState<CreateOrUpdateMetrics_Request[]>([]);
  // Map of kg-columns dependent on logical columns
  const [kgColumnsDependentOnLogicalColumns, setKgColumnsDependentOnLogicalColumns] = useState<Map<string, Header[]>>(new Map());
  // Map of metrics dependent on logical columns
  const [metricsDependentOnLogicalColumns, setMetricsDependentOnLogicalColumns] = useState<Map<string, Header[]>>(new Map());
  // Adding and Removing Mapping requests
  const [mappingRequests, setMappingRequests] = useState<{ [columnIndex: number]: { kgColumn: Header, isAdding: boolean }[] }>({});
  // Track initial KG column mappings for comparison (to detect changes)
  const [initialKgColumnMappings, setInitialKgColumnMappings] = useState<{ [columnIndex: number]: Header | null }>({});

  // Effect to seed model info when editing: reads from context cache when available,
  // falls back to API and then writes the result back into the cache.
  useEffect(() => {
    const fetchModelInfo = async () => {
      if (!id || !updateFlag || modelInfo !== undefined) return;

      // Use cached ModelInfo from context if available — avoids a round-trip
      const cached = modelInfoMap.get(id);
      if (cached) {
        setModelInfo(cached);
        setHeader(cached.header ?? {});
        setColumns(cached.columns ?? []);
        return;
      }

      setLoading(true);
      const data = await getModelById(id);
      if (data) {
        setModelInfo(data);
        setHeader(data.header ?? {});
        setColumns(data.columns ?? []);
        setModelInfoMap((prev) => {
          const next = new Map(prev);
          next.set(id, data);
          return next;
        });
      }
      setLoading(false);
    };
    fetchModelInfo();
  }, [id, updateFlag, modelInfo]);

  // Function to check if the model info has changed
  const hasModelInfoChanged = (): boolean => {
    const originalModelInfo = modelInfo;
    if (!originalModelInfo?.columns) {
      return false;
    }
    const currentColumns = columns;
    const originalColumns = originalModelInfo?.columns;
    if (currentColumns?.length !== originalColumns?.length) {
      return true;
    }
    for (let i = 0; i < originalColumns?.length; i++) {
      const currentColumn = currentColumns[i];

      if (
        currentColumn.header?.description !== originalColumns[i]?.header?.description ||
        currentColumn.column_type !== originalModelInfo?.columns[i]?.column_type ||
        currentColumn.data_type !== originalModelInfo?.columns[i]?.data_type ||
        currentColumn.default_aggregation !== originalModelInfo?.columns[i]?.default_aggregation
      ) {
        return true;
      }
    }
    return false;
  };

  /**
   * Calls the dependency API for the given logical column IDs and writes the result
   * into the context dependencyMap so the models list view doesn't need to re-fetch.
   * Returns the raw Map<logicalColId, Header[]> for further use by the caller.
   */
  const fetchAndCacheDependencies = async (
    modelId: string,
    columnIds: string[]
  ): Promise<Map<string, Header[]>> => {
    const request = DependencyRequestProto.create({
      ids: columnIds,
      metadata_type: MetadataTypeEnum.LOGICAL_COLUMN,
      dependent_types: [
        MetadataTypeEnum.KG_COLUMN,
        MetadataTypeEnum.METRIC,
      ],
      ignore_definition_filters: true,
    });
    const data = await getMetadataDependency(request);
    const rawDepMap = new Map<string, Header[]>();
    if (data) {
      data.forEach((dependency: DependencyProto) => {
        if (dependency.metadata_id && dependency.headers.length > 0) {
          rawDepMap.set(dependency.metadata_id, dependency.headers);
        }
      });
    }
    setDependencyMap((prev) => {
      const next = new Map(prev);
      next.set(modelId, rawDepMap);
      return next;
    });
    return rawDepMap;
  };

  /**
   * Loads dependencies for the edit view. 
   * First check in dependencyMap if the dependencies are already cached.
   * If not, then fetch the dependencies from the API and cache them in the dependencyMap.
   * Then parse the dependencies into the two local component state maps
   * (kgColumnsDependentOnLogicalColumns and metricsDependentOnLogicalColumns) and
   * initialises initialKgColumnMappings for change-tracking during editing.
   */
  const loadLogicalColumnDependencies = async (modelId: string, columnIds: string[], currentColumns: ColumnInfo[]) => {
    const cachedDepMap = dependencyMap.get(modelId);
    const rawDepMap = cachedDepMap ?? await fetchAndCacheDependencies(modelId, columnIds);

    if (!rawDepMap) {
      return;
    }

    const kgColumnsDependencies = new Map<string, Header[]>();
    const metricsDependencies = new Map<string, Header[]>();

    rawDepMap.forEach((headers, logicalColId) => {
      const kgHeaders: Header[] = [];
      const metricHeaders: Header[] = [];
      headers.forEach(h => {
        if (h.type === MetadataTypeEnum.KG_COLUMN) {
          // Need to replace the header with the full header from the kgColumns list
          // because the header from the dependency does not have the column_type field
          const fullHeader = kgColumns?.find(k => k.id === h.id) ?? h;
          kgHeaders.push(fullHeader);
        } else if (h.type === MetadataTypeEnum.METRIC) {
          metricHeaders.push(h);
        }
      });
      if (kgHeaders.length > 0) kgColumnsDependencies.set(logicalColId, kgHeaders);
      if (metricHeaders.length > 0) metricsDependencies.set(logicalColId, metricHeaders);
    });

    setKgColumnsDependentOnLogicalColumns(kgColumnsDependencies);
    setMetricsDependentOnLogicalColumns(metricsDependencies);

    // Initialize initial mappings for change-tracking
    const initialMappings: { [columnIndex: number]: Header | null } = {};
    currentColumns.forEach((column, index) => {
      const mappedKgColumn = kgColumnsDependencies.get(column.header?.id || "");
      initialMappings[index] = mappedKgColumn?.[0] || null;
    });
    setInitialKgColumnMappings(initialMappings);
  };

  useEffect(() => {
    // Fetch KG columns and its dependent metrics and kg-columns when component mounts
    const initializeColumns = async () => {
      if (kgColumns && kgColumns?.length < 0) {
        await fetchKgColumns();
      }
    };
    const modelId = id || modelInfo?.header?.id;
    if (columns.length > 0 && updateFlag && modelId) {
      void loadLogicalColumnDependencies(modelId, columns.map(column => column.header?.id || ""), columns);
    }
    initializeColumns();
  }, [columns]);

  /**
   * Handles changes in the header properties.
   *
   * @param {string} property - The property to be updated.
   * @param {string} value - The new value for the property.
   */
  const handleHeaderChange = (property: string, value: string) => {
    setHeader({
      ...header,
      [property]: value,
    });
  };

  /**
   * Handles changes in the column properties.
   *
   * @param {number} index - The index of the column to be updated.
   * @param {"data_type" | "column_type" | "default_aggregation"} property - The property to be updated.
   * @param {string} value - The new value for the property.
   */
  const handleColumnChange = (
    index: number,
    property: "data_type" | "column_type" | "default_aggregation",
    value: string
  ) => {
    const newColumns = [...columns];
    let parsedValue: DataType | ColumnType | AggregationType | undefined;

    switch (property) {
      case "data_type":
        parsedValue = dataTypeFromJSON(value);
        break;
      case "column_type":
        parsedValue = columnTypeFromJSON(value);
        break;
      case "default_aggregation":
        parsedValue = aggregationTypeFromJSON(value);
        break;
      default:
        parsedValue = undefined;
    }

    newColumns[index] = {
      ...newColumns[index],
      [property]: parsedValue,
    };

    // Dependencies change due to data_type
    if (property === "data_type") {
      let columnType: ColumnType | undefined;
      if ([DataType.VARCHAR, DataType.BOOLEAN].includes(parsedValue as DataType)) {
        columnType = ColumnType.ATTRIBUTE;
        delete newColumns[index].default_aggregation;
      }
      else if ([DataType.INT32, DataType.INT64, DataType.DOUBLE].includes(parsedValue as DataType)) {
        columnType = ColumnType.MEASURE;
        if (!newColumns[index].default_aggregation) {
          newColumns[index].default_aggregation = AggregationType.SUM;
        }
      }
      else if ([DataType.DATE, DataType.DATETIME].includes(parsedValue as DataType)) {
        columnType = ColumnType.TEMPORAL;
        delete newColumns[index].default_aggregation;
      }
      newColumns[index].column_type = columnType;
    }

    // Dependencies change due to column_type
    if (property === "column_type") {
      if ([ColumnType.ATTRIBUTE, ColumnType.TEMPORAL].includes(parsedValue as ColumnType)) {
        delete newColumns[index].default_aggregation;
      }
      else {
        newColumns[index].default_aggregation = AggregationType.SUM;
      }
    }

    setColumns(newColumns);
  };

  /**
   * Handles changes in the KG column header.
   *
   * @param {number} index - The index of the column to be updated.
   * @param {string} kgColumnId - The ID of the selected KG column.
   */
  const handleKGColumnChange = (
    index: number,
    kgColumnId: string,
    newHeader: Header | null
  ) => {
    let selectedKGColumn = kgColumns?.find(col => col.id === kgColumnId);

    if (!selectedKGColumn && newHeader !== null) {
      selectedKGColumn = newHeader;
    }

    // If kgColumnId is empty or "None" was selected, selectedKGColumn will be undefined
    if (!kgColumnId) {
      selectedKGColumn = undefined;
    }

    // Get the current/previous mapping
    const currentMapping = initialKgColumnMappings[index];
    const newMapping = selectedKGColumn || null;

    // Update mapping requests state
    setMappingRequests(prev => {
      const newRequests = { ...prev };

      // Check if there's actually a change
      if (currentMapping?.id !== newMapping?.id) {
        if (newMapping) {
          // Case 1: Assigning a new KG column (first time or changing)
          // Initialize array if it doesn't exist
          if (!newRequests[index]) {
            newRequests[index] = [];
          }

          // If there's a previous mapping, remove it first
          if (currentMapping) {
            newRequests[index].push({
              kgColumn: currentMapping,
              isAdding: false
            });
          }

          // Then add the new KG column mapping
          newRequests[index].push({
            kgColumn: newMapping,
            isAdding: true
          });
        } else {
          // Case 2: Selecting "None" - remove the previous mapping
          if (currentMapping) {
            newRequests[index] = [{
              kgColumn: currentMapping,
              isAdding: false
            }];
          } else {
            // No previous mapping and selecting "None" - no action needed
            delete newRequests[index];
          }
        }
      } else {
        // No change from initial state, remove any pending requests
        delete newRequests[index];
      }

      return newRequests;
    });

    // Update dependentKgColumns to reflect the UI change immediately
    setKgColumnsDependentOnLogicalColumns(prev => {
      const newDependentKgColumns = new Map(prev);
      const logicalColumnId = columns[index].header?.id;

      if (logicalColumnId) {
        if (newMapping) {
          // Set the new KG column mapping
          newDependentKgColumns.set(logicalColumnId, [newMapping]);
        } else {
          // Remove the mapping (selecting "None")
          newDependentKgColumns.delete(logicalColumnId);
        }
      }

      return newDependentKgColumns;
    });
  };


  // Called when a new metric is created for an existing model (updateFlag=true)
  const handleMetricCreatedForColumn = (columnIndex: number, header: Header) => {
    const logicalColumnId = columns[columnIndex]?.header?.id;
    if (!logicalColumnId) return;
    setMetricsDependentOnLogicalColumns(prev => {
      const next = new Map(prev);
      next.set(logicalColumnId, [...(next.get(logicalColumnId) ?? []), header]);
      return next;
    });
  };

  // Handle form submission
  const handleSubmit = async () => {
    try {
      const model_info: ModelInfo = {
        header: header, // Updated header with name and description
        data_source: modelInfo?.data_source, // Unchanged data source info 
        columns: columns
      };

      setButtonLoader(true);
      if (!updateFlag) {
        const data = await createModel(model_info);
        if (data) {
          const modelId = data?.header?.id;  // get the model id from the response for the first time creation of the model
          await handleCreateMetrics(modelId); // create new-metrics for the model.

          // Add Mapping requests for the logical columns and kg-columns.
          await handleMappingRequest();

          // Update modelInfoMap and re-fetch fresh dependency data for this new model
          if (modelId) {
            // Update modelInfoMap with the new model info
            setModelInfoMap((prev) => { const next = new Map(prev); next.set(modelId, data); return next; });
            // Fetch and cache dependencies for the new model
            const columnIds = data.columns.map((c) => c.header?.id).filter((cid): cid is string => Boolean(cid));
            if (columnIds.length > 0) {
              await fetchAndCacheDependencies(modelId, columnIds);
            }
          }

          showToast("Model created successfully! Redirecting to Graph", "", "success");
          onReloadModelList?.();
          navigate("/mkg/models"); // Navigate to the Graph
        }
      }
      else {
        if (metricRequestList.length > 0) {
          setMetricRequestList([]);
        }

        // check if the model info has changed
        const hasModelChanged = hasModelInfoChanged();
        if (hasModelChanged) {
          await updateModel(model_info);
        }
        if (Object.keys(mappingRequests).length > 0) {
          // Handle mapping requests for the updated model as well
          await handleMappingRequest();
        }

        // Update modelInfoMap and re-fetch fresh dependency data for this updated model
        const modelId = model_info.header?.id;
        if (modelId) {
          // Update modelInfoMap with the updated model info
          setModelInfoMap((prev) => { const next = new Map(prev); next.set(modelId, model_info); return next; });
          // Fetch and cache dependencies for the updated model
          const columnIds = model_info.columns.map((c) => c.header?.id).filter((cid): cid is string => Boolean(cid));
          if (columnIds.length > 0) {
            await fetchAndCacheDependencies(modelId, columnIds);
          }
        }

        showToast("Model updated successfully! Redirecting to Graph", "", "success");
        navigate("/mkg/models"); // Navigate to the Graph
      }
      setButtonLoader(false);
    } catch (error) {
      // Handle errors that may occur during the API call
      showToast("Error sending data to API create/update model", "", "error");
      setButtonLoader(false); // Ensure loader is turned off in case of error
    }
  };

  // Accepts a fully-built metric request from the modal and queues it for creation after model save
  const handleQueueMetricRequest = (request: CreateOrUpdateMetrics_Request) => {
    setMetricRequestList(prev => [...prev, request]);
  };

  // Handle Create Metrics
  const handleCreateMetrics = async (modelId?: string) => {
    // If there are no metrics to create, then return without creating any metrics
    if (metricRequestList.length > 0) {
      for (const metricRequest of metricRequestList) {
        const request = CreateOrUpdateMetrics_Request.create({
          model_id: modelId,
          metrics: metricRequest.metrics,
          update_edges: false,
        });
        const response = await createMetric(request);
        if (response) {
          showToast(`${response.header?.name} Metric created successfully`, "", "success");
        }
      }
    } else {
      // then return without creating any metrics
      return;
    }
  };

  // Handle Add Mapping or Remove Mapping request
  const handleMappingRequest = async () => {
    try {
      // Process each mapping request
      for (const [columnIndexStr, requestArray] of Object.entries(mappingRequests)) {
        const columnIndex = parseInt(columnIndexStr);
        const logicalColumn = columns[columnIndex];
        const logicalColumnId = logicalColumn.header?.id;

        if (!logicalColumnId) {
          continue;
        }

        // Process each request in the array for this column
        for (const request of requestArray) {
          // Create KGColumnProto
          const kgColumnProto = KGColumnProto.create({
            header: request.kgColumn
          });

          if (request.isAdding) {
            // Add mapping
            await updateKGColumn(
              null,
              kgColumnProto.header?.id, // kgColumnId
              [logicalColumnId], // addMappings
              [], // removeMappings
              false // updateLogicalMappings
            );
          } else {
            // Remove mapping
            await updateKGColumn(
              null,
              kgColumnProto.header?.id, // kgColumnId
              [], // addMappings
              [logicalColumnId], // removeMappings
              false // updateLogicalMappings
            );
          }
        }
      }

      // Clear mapping requests after processing
      setMappingRequests({});
    } catch (error) {
      console.error("Error processing mapping requests:", error);
      showToast("Error updating KG column mappings", "", "error");
    }
  };

  return (
    <Box h="100%" minH={0} overflow="hidden" display="flex" flexDirection="column">
      <OntologyMapping
        loading={loading}
        modelInfo={modelInfo}
        addedColumns={addedColumns}
        removedColumns={removedColumns}
        header={header}
        columns={columns}
        dependentKgColumns={kgColumnsDependentOnLogicalColumns}
        dependentMetrics={metricsDependentOnLogicalColumns}
        handleHeaderChange={handleHeaderChange}
        handleColumnChange={handleColumnChange}
        onKgSelectionChange={(idx, h) => handleKGColumnChange(idx, h?.id ?? "", h)}
        handleSubmit={handleSubmit}
        updateFlag={updateFlag}
        buttonLoader={buttonLoader}
        kgColumns={kgColumns ?? []}
        refreshKGColumns={fetchKgColumns}
        handleQueueMetricRequest={handleQueueMetricRequest}
        onMetricCreated={handleMetricCreatedForColumn}
        metricRequestList={metricRequestList}
        onBack={onBack}
      />
    </Box>
  );
};

export default OntologyMappingContainer;
